
import { supabase } from "@/integrations/supabase/client";
import { FollowStrategyData } from "@/types/strategy-types";
import { toast } from "sonner";

/**
 * Stores a followed strategy to the user's account
 * @param data Object containing strategy and userId
 * @returns True if successful, false otherwise
 */
export const storeFollowedStrategy = async (data: FollowStrategyData & { userId?: string }): Promise<boolean> => {
  try {
    // Extract strategy and user data
    const { strategy, userId } = data;
    
    if (!userId) {
      console.error("Cannot follow strategy: Missing user ID", data);
      toast("Error following strategy", {
        description: "Authentication required. Please sign in to follow strategies."
      });
      return false;
    }
    
    // Extract strategy ID, ensuring we have valid data
    const strategyId = strategy.id || strategy.strategy_id;
    
    if (!strategyId) {
      console.error("Cannot follow strategy: Missing strategy ID", strategy);
      toast("Error following strategy", {
        description: "Strategy information is incomplete. Please try again."
      });
      return false;
    }
    
    // Check if strategy is already followed
    const { data: existingFollows, error: checkError } = await supabase
      .from('followed_strategies')
      .select('*')
      .eq('user_id', userId)
      .eq('strategy_id', strategyId)
      .limit(1);
    
    if (checkError) {
      console.error("Error checking for existing follows:", checkError);
      return false;
    }
    
    // If already followed, don't insert again
    if (existingFollows && existingFollows.length > 0) {
      console.log("Strategy already followed by user");
      return true;
    }
    
    // Simplified data for insertion - only storing the relationship
    const followData = {
      user_id: userId,
      strategy_id: strategyId
    };
    
    console.log("Storing followed strategy with data:", followData);
    
    // Store strategy with user ID
    const { error } = await supabase
      .from('followed_strategies')
      .insert([followData]);
    
    if (error) {
      console.error("Error storing followed strategy:", error);
      toast("Error following strategy", {
        description: "Failed to save the strategy to your account."
      });
      return false;
    }
    
    toast("Strategy followed successfully", {
      description: "You can view it in your account page."
    });
    
    return true;
  } catch (err) {
    console.error("Unexpected error storing followed strategy:", err);
    toast("Error following strategy", {
      description: "An unexpected error occurred."
    });
    return false;
  }
};
